import { useState, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import { useAuth } from '../../auth/auth-provider';
import { ThemeToggle } from '../ui/theme-toggle';
import {
  GridIcon,
  UserCircleIcon,
  ListIcon,
  CalenderIcon,
  CloseIcon,
  BoxCubeIcon,
  ListIcon as MenuIcon,
} from '../../vendor/tailadmin/icons';

/**
 * Partner portal shell (#72158 + #72487 stroje).
 *
 * A deliberately lightweight, self-contained chrome for the partner-facing
 * portal — separate from the internal admin/decision_maker AppLayout. The
 * partner sees a focused set of links (Přehled / Profil / Stroje / Zakázky /
 * Kalendář), a slim top bar (brand + theme + sign-out), and a mobile drawer.
 * No global search, no breadcrumb stack, no admin nav — the goal is a simple,
 * mobile-first surface (MVP scope: responsive mobile/tablet/desktop).
 *
 * Routing-wise this wraps every `/portal/*` page. Role enforcement is done by
 * `RequireRole` in the route tree + the backend scope filter; this component
 * only renders the chrome.
 */

interface PortalNavItem {
  to: string;
  label: string;
  icon: typeof GridIcon;
}

const PORTAL_NAV: readonly PortalNavItem[] = [
  { to: '/portal', label: 'Přehled', icon: GridIcon },
  { to: '/portal/profil', label: 'Profil', icon: UserCircleIcon },
  { to: '/portal/stroje', label: 'Stroje', icon: BoxCubeIcon },
  { to: '/portal/zakazky', label: 'Zakázky', icon: ListIcon },
  { to: '/portal/kalendar', label: 'Kalendář', icon: CalenderIcon },
];

export function PortalLayout({ children }: { children: ReactNode }) {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <PortalHeader onOpenDrawer={() => setDrawerOpen(true)} />
      <div className="mx-auto flex w-full max-w-(--breakpoint-xl) gap-6 px-4 py-6 md:px-6">
        {/* Desktop sidebar */}
        <aside className="hidden w-56 shrink-0 lg:block">
          <PortalNav />
        </aside>
        {/* Mobile drawer */}
        {drawerOpen ? <PortalDrawer onClose={() => setDrawerOpen(false)} /> : null}
        <main className="min-w-0 flex-1">{children}</main>
      </div>
    </div>
  );
}

function PortalHeader({ onOpenDrawer }: { onOpenDrawer: () => void }) {
  const auth = useAuth();
  const navigate = useNavigate();

  const onSignOut = async () => {
    await auth.logout();
    void navigate({ to: '/login' });
  };

  return (
    <header className="sticky top-0 z-40 border-b border-gray-200 bg-white/90 backdrop-blur dark:border-gray-800 dark:bg-gray-900/90">
      <div className="mx-auto flex h-14 w-full max-w-(--breakpoint-xl) items-center justify-between px-4 md:px-6">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onOpenDrawer}
            aria-label="Otevřít menu"
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 lg:hidden dark:text-gray-300 dark:hover:bg-gray-800"
          >
            <MenuIcon className="h-5 w-5" />
          </button>
          <Link to="/portal" className="flex items-center gap-2">
            <span className="text-base font-semibold text-brand-700 dark:text-brand-400">
              TENA EDM
            </span>
            <span className="hidden text-xs font-medium text-gray-400 sm:inline">
              Partnerský portál
            </span>
          </Link>
        </div>
        <div className="flex items-center gap-2">
          {auth.user?.email ? (
            <span className="hidden max-w-[180px] truncate text-sm text-gray-500 sm:inline dark:text-gray-400">
              {auth.user.email}
            </span>
          ) : null}
          <ThemeToggle />
          <button
            type="button"
            onClick={() => void onSignOut()}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-white/[0.05]"
          >
            Odhlásit
          </button>
        </div>
      </div>
    </header>
  );
}

function PortalNav({ onNavigate }: { onNavigate?: () => void }) {
  const location = useLocation();
  return (
    <nav className="space-y-1">
      {PORTAL_NAV.map((item) => {
        // Exact match for the index, prefix match for sub-routes.
        const active =
          item.to === '/portal'
            ? location.pathname === '/portal'
            : location.pathname.startsWith(item.to);
        const Icon = item.icon;
        return (
          <Link
            key={item.to}
            to={item.to}
            onClick={onNavigate}
            className={`relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              active
                ? 'text-brand-600 before:absolute before:inset-y-2 before:left-0 before:w-[3px] before:rounded-full before:bg-brand-500 dark:text-brand-400'
                : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
            }`}
          >
            <Icon className="h-5 w-5 shrink-0" />
            {item.label}
          </Link>
        );
      })}
    </nav>
  );
}

function PortalDrawer({ onClose }: { onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      <div className="absolute inset-0 bg-gray-900/40" onClick={onClose} aria-hidden="true" />
      <div className="absolute left-0 top-0 h-full w-64 bg-white p-4 shadow-xl dark:bg-gray-900">
        <div className="mb-4 flex items-center justify-between">
          <span className="text-base font-semibold text-brand-700 dark:text-brand-400">
            TENA EDM
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Zavřít menu"
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>
        <PortalNav onNavigate={onClose} />
      </div>
    </div>
  );
}
